import React from 'react';
import { useTranslation } from 'react-i18next';
import { Title } from '../components/ui/Title';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SEOHead } from '../components/SEOHead';
import faqImage from '../assets/faq.avif';

interface FAQItem {
  question: string;
  answer: string;
}

function FAQ() {
  const { t } = useTranslation();
  const [openItems, setOpenItems] = React.useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const faqItems: FAQItem[] = t('faq.items', { returnObjects: true }) as FAQItem[];

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="FAQ"
        description={`Häufig gestellte Fragen und Antworten zu jamnation.`}
        keywords={[
          'FAQ',
          'Musiker',
          'Bands',
          'Schweizer Musiker',
          'Musik Schweiz',
          'Musiker Schweiz'
        ].filter(Boolean)}
        url={`https://jamnation.ch/faq`}
        type="faq"
        structuredData={structuredData}
      />

      <div className="container mx-auto px-6 py-8 md:py-24">
        <Title 
          subtitle={t('faq.subtitle')}
        >
          {t('faq.title')}
        </Title>
        
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <motion.div
                key={index}
                className="panel"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full flex items-center justify-between text-left p-6 hover:bg-gray-custom/5 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-light pr-4">
                    {item.question}
                  </h3>
                  {openItems.includes(index) ? (
                    <ChevronUp className="w-5 h-5 text-light flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-light flex-shrink-0" />
                  )}
                </button>
                
                <AnimatePresence>
                  {openItems.includes(index) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-6">
                        <div 
                          className="text-light/80 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: item.answer }}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* Teaser: Any Questions */}
          <div className="max-w-4xl mx-auto mt-24">
            <div className="grid md:grid-cols-3 gap-0 items-stretch bg-gray-custom/10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden">
              {/* Image Section - 1/3 width */}
              <div className="md:col-span-1 relative h-80 md:h-96">
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{
                    backgroundImage: `url(${faqImage})`,
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-secondary/10 to-secondary/40" />
                {/* Fade effect from left to right */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-gray-custom/10" />
              </div>
              
              {/* Content Section - 2/3 width */}
              <div className="md:col-span-2 p-6 md:p-12 flex flex-col justify-center">
                <h2 className="font-tilt-warp text-3xl md:text-5xl text-light mb-6">
                  {t('faq.stillHaveQuestions')}
                </h2>
                <p className="text-light/60 text-lg mb-8 leading-relaxed">
                  {t('faq.contactMessage')}
                </p>
                <a
                  href="mailto:info@jamnation.ch"
                  className="btn-secondary w-fit"
                >
                  {t('faq.contactUs')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default FAQ;